import type { TFunction } from 'i18next';
import type { MeasureDeadline } from '../../../../api/measureDeadlines';
import { dayAndMonth } from '../../../WorkEnvironmentSurveyDetail/surveyProcessView';
import { fullDate } from '../../hmsFormat';

/*
 * Which measures HR follows, and who can be reminded, is decided in the API.
 * What is here writes it out for the table.
 */

export const isOverdue = (measure: MeasureDeadline) => measure.daysLeft < 0;

/** The API only returns measures past the deadline or due within two weeks */
export const deadlineCounts = (measures: MeasureDeadline[]) => {
  const overdue = measures.filter(isOverdue).length;
  return { overdue, soon: measures.length - overdue };
};

export const deadlineTag = (measure: MeasureDeadline, t: TFunction) =>
  measure.daysLeft < 0
    ? t('hrMeasureDeadlines.over', { count: -measure.daysLeft })
    : measure.daysLeft === 0
      ? t('hrMeasureDeadlines.today')
      : t('hrMeasureDeadlines.left', { count: measure.daysLeft });

export const dueDateText = (measure: MeasureDeadline, language: string) =>
  fullDate(measure.dueDate, language);

export const ownerText = (measure: MeasureDeadline, t: TFunction) =>
  measure.owner
    ? t(`hrMeasureDeadlines.roles.${measure.owner.role}`, {
        email: measure.owner.email,
      })
    : t('hrMeasureDeadlines.noOwner');

export const ownerStatus = (
  measure: MeasureDeadline,
  t: TFunction,
  language: string,
) =>
  measure.reminded.owner
    ? t('hrMeasureDeadlines.reminded', {
        date: dayAndMonth(measure.reminded.owner, language),
      })
    : t('hrMeasureDeadlines.notReminded');

export const safetyRepText = (measure: MeasureDeadline, t: TFunction) =>
  measure.safetyReps.length > 0
    ? measure.safetyReps.join(', ')
    : t('hrMeasureDeadlines.noSafetyRep');

/** The safety representatives hear about it once the deadline has passed */
export const safetyRepStatus = (
  measure: MeasureDeadline,
  t: TFunction,
  language: string,
) => {
  if (measure.safetyReps.length === 0)
    return t(
      measure.safetyRepMissing === 'leaderIsSafetyRep'
        ? 'hrMeasureDeadlines.leaderIsSafetyRep'
        : 'hrMeasureDeadlines.teamHasNoSafetyRep',
    );
  if (measure.reminded.safetyRep)
    return t('hrMeasureDeadlines.notified', {
      date: dayAndMonth(measure.reminded.safetyRep, language),
    });
  return t(
    isOverdue(measure)
      ? 'hrMeasureDeadlines.notNotified'
      : 'hrMeasureDeadlines.notifiedIfLate',
  );
};

/** Draws attention where nobody has been told yet */
export const needsAttention = (measure: MeasureDeadline) => ({
  owner: !measure.reminded.owner,
  safetyRep:
    measure.safetyReps.length === 0 ||
    (isOverdue(measure) && !measure.reminded.safetyRep),
});
